import { NAMESPACE, QUIZ_PAGE } from "@modules/editor/service/editor.constant";
import { LESSON_ACTIVITY_TYPE } from "@plugins/common/constant/resource.constant.ts";
import { mapGetters, mapState } from "vuex";
import { isNull } from "@utils/var.check";
import AssignmentUtils from "@modules/assignment/service/assignment.utils";
import StringUtils from "@utils/string.utils";
import quizNavigatorStore from "@plugins/quiz-navigator/components/QuizNavigatorStore";
import ActivityComponentStore from "@modules/editor/components/activity/ActivityComponentStore";
import DeviceUtils from "@utils/device.utils";
import { ROUTES_VIEW } from "@/modules/attempt/service/attempt.constant";

export default {
  data() {
    return {
      quizNavigatorStore: quizNavigatorStore,
      activityComponentStore: ActivityComponentStore,
    };
  },
  computed: {
    ...mapState(NAMESPACE.LESSON, {
      lesson: (state) => state.lesson,
    }),
    ...mapGetters(NAMESPACE.LESSON, [
      "currentPageId",
      "currentSectionId",
      "currentSection",
      "hideSectionActivityNumber",
      "getActivity",
      "getActivityList",
      "resourceDomain",
      "isAdminPage",
      "targetLessonStatus",
      "getSectionList",
      "getLeafComponentsInActivity",
      "showSectionCover",
      "getComponentsByContainerId",
    ]),
    ...mapGetters(NAMESPACE.EDITOR, ["currentMode"]),
    isSectionActivityNumberHidden() {
      return !this.isEditorInEditMode && this.hideSectionActivityNumber;
    },
    activityList() {
      return this.getActivityList(this.currentSectionId);
    },
    sectionData() {
      return this.currentSection ? this.currentSection : {};
    },
    nextSectionData() {
      if (this.isLastSection) {
        return {};
      }
      const sectionList = this.getSectionList;
      const nextSection = sectionList.find(
        (section) => section.pageNo === this.currentSection.pageNo + 1,
      );
      return nextSection || {};
    },
    fromCover() {
      return this.currentPageId === "cover";
    },
    fromModulePlan() {
      return this.currentPageId === "modulePlan";
    },
    fromSectionCover() {
      return this.isActiveSectionCoverPage(this.currentSectionId);
    },
    fromActivity() {
      return !!this.getActivity(this.currentPageId);
    },
    isActiveSectionCoverPage() {
      return (sectionId) => {
        return this.currentPageId === "sectionCover" && this.currentSectionId === sectionId;
      };
    },
    isActiveActivityPage() {
      return (activityId) => {
        return this.currentPageId === activityId.toString();
      };
    },
    isFirstSection() {
      return this.sectionData.pageNo === 1;
    },
    isLastSection() {
      return this.sectionData.pageNo === this.getSectionList.length;
    },
    sectionLabel() {
      return this.currentSection
        ? StringUtils.getAlphabeticalLetter(this.currentSection.pageNo - 1)
        : "";
    },
    nextSectionLabel() {
      return this.currentSection
        ? StringUtils.getAlphabeticalLetter(this.currentSection.pageNo)
        : "";
    },
    hasSections() {
      return this.getSectionList.length > 0;
    },
    hasActivities() {
      let hasActivities = false;
      this.getSectionList.every((section) => {
        if (this.getActivityList(section.id).length > 0) {
          hasActivities = true;
          return false;
        }
        return true;
      });
      return hasActivities;
    },
    activitiesInFirstSection() {
      const firstSection = this.getSectionList.find((section) => {
        return section.pageNo === 1;
      });
      return this.getActivityList(firstSection.id);
    },
    previousNavigationSectionLabel() {
      return this.isFirstSection
        ? this.$i18n.t("course_plan_introduction_label")
        : this.currentSection
          ? this.previousSectionLastActivityLabel
          : "";
    },
    nextNavigationSectionLabel() {
      if (this.activityList.length > 0) {
        const firstActivity = this.activityList[0];

        if (firstActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
          return this.getBottomNavigationActivityLabel(firstActivity.pageNo, firstActivity.title);
        } else if (firstActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY) {
          if (this.isActivityHasPagination(firstActivity.id)) {
            return this.getBottomNavigationActivityPageLabel(
              firstActivity.pageNo,
              firstActivity.title,
              1,
            );
          }
          return this.getBottomNavigationActivityLabel(firstActivity.pageNo, firstActivity.title);
        }
      } else {
        const nextPageIndex = this.sectionData.pageNo;
        const nextSection = this.getSectionList[nextPageIndex];
        if (nextPageIndex < this.getSectionList.length) {
          return this.getBottomNavigationSectionDefaultLabel(
            nextSection.pageNo - 1,
            nextSection.title,
          );
        }
      }
    },

    previousSectionLastActivityLabel() {
      if (this.isFirstSection) {
        return "";
      } else {
        const previousPageIndex = this.sectionData.pageNo - 2; // Index for the previous section
        const previousSection = this.getSectionList[previousPageIndex];
        const activityList = this.getActivityList(previousSection.id);

        if (activityList.length > 0) {
          const lastActivity = activityList[activityList.length - 1];
          const activityNumber = lastActivity.pageNo;

          if (lastActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
            return this.getBottomNavigationActivityQuizEndLabel(activityNumber);
          } else if (lastActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY) {
            const lastActivityLastPageNo = this.isActivityHasPagination(lastActivity.id)
              ? this.getActivityPageNo(lastActivity.id)
              : null;

            if (this.isActivityHasPagination(lastActivity.id)) {
              return this.getBottomNavigationActivityPageLabel(
                activityNumber,
                lastActivity.title,
                lastActivityLastPageNo,
              );
            } else {
              return this.getBottomNavigationActivityLabel(activityNumber, lastActivity.title);
            }
          }
        }

        return this.getBottomNavigationSectionDefaultLabel(
          previousSection.pageNo - 1,
          previousSection.title,
        );
      }
    },
  },
  methods: {
    getActivityPageNo(pageId) {
      return this.getComponentsByContainerId(0, pageId.toString())
        ? this.getComponentsByContainerId(0, pageId.toString()).length
        : 0;
    },
    isActivityHasPagination(pageId) {
      const containers = this.getComponentsByContainerId(0, pageId.toString());
      return containers.length > 1;
    },
    processRouteQuery({ isNavigateToFirstActivity, openLessonSettings, showAuthoringCoPilot }) {
      const routeQuery = {};

      // mandatory route params:
      // register if given, otherwise ignore (use existing)
      if (isNavigateToFirstActivity)
        routeQuery.isNavigateToFirstActivity = isNavigateToFirstActivity;
      if (openLessonSettings) routeQuery.openLessonSettings = openLessonSettings;
      if (this.$route?.query?.assignmentUuid ?? false) {
        routeQuery.assignmentUuid = this.$route.query.assignmentUuid;
      }
      if (this.$route?.query?.version ?? false) {
        routeQuery.version = this.$route.query.version;
      }
      if (this.$route?.query?.openGameSubpage) {
        routeQuery.openGameSubpage = this.$route.query.openGameSubpage;
      }
      if (this.$route?.query?.openAnnotation) {
        routeQuery.openAnnotation = this.$route.query.openAnnotation;
      }
      if (showAuthoringCoPilot) {
        routeQuery.showAuthoringCoPilot = showAuthoringCoPilot;
      }
      return routeQuery;
    },
    // TODO: SLS-11043 (OK), editor
    processRouteParams({
      resourceDomain,
      mode,
      lessonUuid,
      sectionId,
      pageId,
      isAdminPage,
      status,
      openGameSubpage,
      pageNo,
      showAuthoringCoPilot,
    }) {
      const routeParams = {};

      // mandatory route params:
      // register if given, otherwise ignore (use existing)
      if (resourceDomain) routeParams.resourceDomain = resourceDomain;
      if (mode) routeParams.mode = mode;
      if (lessonUuid) routeParams.lessonUuid = lessonUuid;
      if (pageId) routeParams.pageId = pageId;
      if (sectionId) routeParams.sectionId = sectionId;
      if (pageNo) routeParams.pageNo = pageNo;
      if (this.$route.params.fromLocation)
        routeParams.fromLocation = this.$route.params.fromLocation;
      if (openGameSubpage) routeParams.openGameSubpage = openGameSubpage;
      if (showAuthoringCoPilot) routeParams.showAuthoringCoPilot = showAuthoringCoPilot;

      // optional route params:
      // register if given, otherwise check the current value
      if (
        (isDefined(isAdminPage) && isAdminPage) ||
        (notDefined(isAdminPage) && this.isAdminPage)
      ) {
        routeParams.admin = "admin";
      }

      if (isDefined(status)) {
        routeParams.status = status.toLowerCase();
      } else if (notDefined(status) && this.targetLessonStatus) {
        routeParams.status = this.targetLessonStatus.toLowerCase();
      }

      return routeParams;
    },
    getEditorPageRoute(params) {
      return {
        name: "EditorActivityPage",
        params: this.processRouteParams(params),
        query: this.processRouteQuery(params),
      };
    },
    routeToEditorPage(params) {
      return this.$router.push({
        name: "EditorActivityPage",
        params: this.processRouteParams(params),
        query: this.processRouteQuery(params),
      });
    },
    getEditorCoverRoute(params) {
      return {
        name: "EditorCover",
        params: this.processRouteParams(params),
        query: this.processRouteQuery(params),
      };
    },
    routeToEditorCover(params) {
      return this.$router.push({
        name: "EditorCover",
        params: this.processRouteParams(params),
        query: this.processRouteQuery(params),
      });
    },
    getEditorEndRoute(params) {
      return {
        name: "EditorEnd",
        params: this.processRouteParams(params),
        query: this.processRouteQuery(params),
      };
    },
    getEditorModulePlanRoute(params) {
      return {
        name: "EditorModulePlan",
        params: this.processRouteParams(params),
        query: this.processRouteQuery(params),
      };
    },

    getEditorModulePlanReorderRoute(params) {
      return {
        name: "EditorModulePlanReorder",
        params: this.processRouteParams(params),
        query: this.processRouteQuery(params),
      };
    },

    getEditorSectionCoverRoute(params) {
      return {
        name: "EditorSectionCover",
        params: this.processRouteParams(params),
        query: this.processRouteQuery(params),
      };
    },
    routeToEditorSectionCover(params) {
      return this.$router.push({
        name: "EditorSectionCover",
        params: this.processRouteParams(params),
        query: this.processRouteQuery(params),
      });
    },
    routeToNotFoundPage() {
      this.$router.push({ name: "EditorNotFoundPage" });
    },
    getAssignmentViewRouteByCurrentPageId() {
      const { eAssessment, lessonAssignmentUuid, uuid } = this.lesson;
      const routes = eAssessment ? ROUTES_VIEW.ASSESSMENT : ROUTES_VIEW.ASSIGNMENT;
      const pageId = this.currentPageId;
      const params = { assignmentUuid: lessonAssignmentUuid, moduleUuid: uuid };

      let route = routes.COVER_PAGE;

      if (pageId === "sectionCover") {
        route = routes.SECTION_COVER_PAGE;
        params.sectionId = this.currentSectionId;
      } else if (pageId === "cover") {
        route = routes.COVER_PAGE;
      } else if (pageId === "modulePlan") {
        route = routes.MODULE_PLAN;
      } else if (pageId) {
        route = routes.ACTIVITY_PAGE;
        params.sectionId = this.currentSectionId;
        params.pageId = pageId;
      }

      return { name: route, params };
    },
    getRouteByCurrentPageId(switchMode) {
      let route = {};
      const pageId = this.currentPageId;
      const routeParams = {
        mode: switchMode ? switchMode : this.currentMode,
      };

      if (pageId === "sectionCover") {
        route = this.getEditorSectionCoverRoute(routeParams);
      } else if (pageId === "cover") {
        route = this.getEditorCoverRoute(routeParams);
      } else if (pageId === "end") {
        route = this.getEditorEndRoute(routeParams);
      } else if (pageId === "modulePlan") {
        route = this.getEditorModulePlanRoute(routeParams);
      } else if (pageId === "modulePlanReorder") {
        route =
          routeParams.mode === "view"
            ? this.getEditorModulePlanRoute(routeParams)
            : this.getEditorModulePlanReorderRoute(routeParams);
      } else if (pageId !== "") {
        // TODO: SLS-11043 (DONE added section id), editor, add sectionId
        const activity = this.getActivity(pageId);
        const sectionId = `${activity.parentId}`;
        routeParams.sectionId = sectionId;
        route = this.getEditorPageRoute(routeParams);
      } else {
        route = this.getEditorCoverRoute(routeParams);
      }
      return route;
    },

    handlePrevious(currentPage) {
      if (isNull(currentPage)) {
        // completion page
        if (this.navigateLessonEndPrevious()) return;
      }
      if (AssignmentUtils.isActivityOrQuizActivity(currentPage.type)) {
        if (this.navigatePreviousActivity(currentPage)) return;
      }
      if (currentPage.type === LESSON_ACTIVITY_TYPE.SECTION) {
        if (this.navigatePreviousSection()) return;
      }
      return this.$router.push(this.getEditorCoverRoute({}));
    },

    handleNext(currentPage) {
      if (isNull(currentPage)) {
        // introduction page
        if (this.navigateLessonCoverNext()) return;
      }
      if (AssignmentUtils.isActivityOrQuizActivity(currentPage.type)) {
        if (this.navigateNextActivity(currentPage)) return;
      }
      if (currentPage.type === LESSON_ACTIVITY_TYPE.SECTION) {
        if (this.navigateNextSection()) return;
      }
      return this.$router.push(this.getEditorEndRoute({}));
    },

    navigateLessonCoverNext() {
      const nextPageIndex = 0;
      // if section cover hidden, route to first activity
      if (!this.showSectionCover) {
        const page = this.activitiesInFirstSection[0];
        return this.goToByPageId(page.id);
      }

      // go to first section cover in course
      const section = this.getSectionList[nextPageIndex];
      return this.$router.push(this.getEditorSectionCoverRoute({ sectionId: section.id }));
    },

    navigateLessonEndPrevious() {
      const previousPageIndex = this.activityList.length - 1;
      if (previousPageIndex >= 0) {
        return this.goToByPageId(this.activityList[previousPageIndex].id);
      }
      if (this.activityList.length === 0 && this.sectionData) {
        // go to last section cover for section with no activity
        return this.$router.push(
          this.getEditorSectionCoverRoute({ sectionId: this.sectionData.id }),
        );
      }
    },
    navigatePreviousActivity(currentPage) {
      if (currentPage.pageNo >= 2) {
        //go to previous activity
        const previousPageIndex = currentPage.pageNo - 2;
        if (previousPageIndex >= 0) {
          return this.goToByPageId(this.activityList[previousPageIndex].id);
        }
      } else if (currentPage.parentId !== null) {
        //go to section cover
        const section = this.getSectionList.find((section) => {
          return section.id === currentPage.parentId;
        });
        return this.$router.push(this.getEditorSectionCoverRoute({ sectionId: section.id }));
      }
    },
    navigatePreviousSectionCover() {
      const previousPageIndex = this.sectionData.pageNo - 2;
      if (previousPageIndex < this.getSectionList.length) {
        const previousSectionId = this.getSectionList[previousPageIndex].id;
        return this.$router.push(this.getEditorSectionCoverRoute({ sectionId: previousSectionId }));
      }
    },
    navigatePreviousSection() {
      //go to previous section
      const previousPageIndex = this.sectionData.pageNo - 2;
      if (previousPageIndex >= 0) {
        const previousSection = this.getSectionList[previousPageIndex];
        const activityList = this.getActivityList(previousSection.id);
        if (activityList.length > 0) {
          //go to last activity in previous section
          return this.goToByPageId(activityList[activityList.length - 1].id);
        } else {
          //go to previous section cover
          const previousSectionId = previousSection.id;
          return this.$router.push(
            this.getEditorSectionCoverRoute({ sectionId: previousSectionId }),
          );
        }
      }
    },
    navigateNextActivity(currentPage) {
      if (currentPage.pageNo < this.activityList.length) {
        //go to next activity
        const nextPageIndex = currentPage.pageNo;
        if (nextPageIndex < this.activityList.length) {
          return this.goToByPageId(this.activityList[nextPageIndex].id);
        }
      } else if (currentPage.parentId !== null) {
        //go to next section cover
        const section = this.getSectionList.find((section) => {
          return section.id === currentPage.parentId;
        });
        if (section.pageNo < this.getSectionList.length) {
          const nextSectionId = this.getSectionList[section.pageNo].id;
          return this.$router.push(this.getEditorSectionCoverRoute({ sectionId: nextSectionId }));
        }
      }
    },
    navigateNextSection() {
      //go to first activity in section
      if (this.activityList.length !== 0) {
        return this.goToByPageId(this.activityList[0].id);
      } else {
        return this.navigateNextSectionCover();
      }
    },
    navigateNextSectionCover() {
      //go to next section cover page
      const nextPageIndex = this.sectionData.pageNo;
      if (nextPageIndex < this.getSectionList.length) {
        const nextSectionId = this.getSectionList[nextPageIndex].id;
        return this.$router.push(this.getEditorSectionCoverRoute({ sectionId: nextSectionId }));
      }
    },
    goToCover() {
      return this.$router.push(this.getEditorCoverRoute({}));
    },
    goToSectionCover(sectionId, showAuthoringCoPilot) {
      return this.$router.push({
        name: "EditorSectionCover",
        // TODO: SLS-11043 (OK), editor
        params: this.processRouteParams({ sectionId: sectionId }),
        query: this.processRouteQuery({ showAuthoringCoPilot: showAuthoringCoPilot }),
      });
    },
    goToByPageId(pageId, showAuthoringCoPilot) {
      const activity = this.getActivity(pageId);
      const sectionId = `${activity.parentId}`;
      return this.$router.push({
        name: "EditorActivityPage",
        // TODO: SLS-11043 (DONE added section id), editor, add sectionId
        params: this.processRouteParams({ sectionId, pageId: pageId }),
        query: this.processRouteQuery({
          pageId: pageId,
          showAuthoringCoPilot: showAuthoringCoPilot,
        }), // to retain route query params
      });
    },
    goToByPageIdAndQuizPageNo(pageId, pageNo) {
      const activity = this.getActivity(pageId);
      const sectionId = `${activity.parentId}`;
      const query = { quizPage: pageNo };
      if (this.$route?.query?.version) {
        query.version = this.$route.query.version;
      }
      return this.$router.push({
        name: "EditorActivityPage",
        // TODO: SLS-11043 (DONE added section id), editor, add sectionId
        params: this.processRouteParams({ sectionId, pageId: pageId }),
        query,
      });
    },
    goToByPageIdAndPageNo(pageId, pageNo) {
      const activity = this.getActivity(pageId);
      const sectionId = `${activity.parentId}`;
      const query = { pageNo: pageNo };
      if (this.$route?.query?.version) {
        query.version = this.$route.query.version;
      }
      return this.$router.push({
        name: "EditorActivityPage",
        // TODO: SLS-11043 (DONE added section id), editor, add sectionId
        params: this.processRouteParams({ sectionId, pageId: pageId }),
        query,
      });
    },
    showAlpStageDisplayName(item) {
      return (
        item && item.alpStage && item.alpStage.displayName && item.alpStage.displayName !== "Custom"
      );
    },
    alpStageDisplayName(item) {
      return item && item.alpStage && item.alpStage.displayName ? item.alpStage.displayName : "";
    },
    confirmExit(customRoute) {
      if (customRoute === "classGroup") {
        const props = {
          title: this.$i18n.t("lesson_exit_confirmation_title"),
          content: this.$i18n.t("lesson_exit_confirmation", {
            destination: this.$i18n.t("group_label"),
          }),
          primaryBtnLabel: this.$i18n.t("button_edit_ok"),
          use_secondaryButton: this.$i18n.t("button_edit_cancel"),
          actionPrimary: () => {
            this.$router.push({
              name: "StudentGroup",
              params: { collectionUuid: this.studentGroupCollectionUuid },
            });
          },
        };
        this.$modal.showMessage("warning", props);
      } else if (customRoute === "dashboard") {
        const props = {
          title: this.$i18n.t("lesson_exit_confirmation_title"),
          content: this.$i18n.t("lesson_exit_confirmation", {
            destination: this.$i18n.t("homepage_label"),
          }),
          primaryBtnLabel: this.$i18n.t("button_edit_ok"),
          use_secondaryButton: this.$i18n.t("button_edit_cancel"),
          actionPrimary: () => {
            this.$router.push({ name: "Dashboard" });
          },
        };
        this.$modal.showMessage("warning", props);
      }
    },
    navigateSectionPrevious() {
      if (this.isFirstSection) {
        // If the current section is the first section, navigate to the editor cover
        return this.$router.push(this.getEditorCoverRoute({}));
      } else {
        // Go to the previous section
        const previousPageIndex = this.sectionData.pageNo - 2;
        if (previousPageIndex >= 0) {
          const previousSection = this.getSectionList[previousPageIndex];
          const activityList = this.getActivityList(previousSection.id);

          if (activityList.length > 0) {
            // Go to the last activity in the previous section
            const prevSectionLastActivity = activityList[activityList.length - 1];

            if (prevSectionLastActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
              this.quizNavigatorStore.setCurrentPage(
                prevSectionLastActivity.id,
                QUIZ_PAGE.QUIZ_END,
              );
              return this.goToByPageIdAndQuizPageNo(prevSectionLastActivity.id, QUIZ_PAGE.QUIZ_END);
            } else if (prevSectionLastActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY) {
              if (this.isActivityHasPagination(prevSectionLastActivity.id)) {
                this.activityComponentStore.setCurrentPageNo(
                  prevSectionLastActivity.uuid,
                  this.getActivityPageNo(prevSectionLastActivity.id),
                );
                this.goToByPageIdAndPageNo(
                  prevSectionLastActivity.id,
                  this.getActivityPageNo(prevSectionLastActivity.id),
                );
              } else {
                this.goToByPageId(prevSectionLastActivity.id);
              }
            }
          } else {
            // If no activities, go to the previous section cover
            return this.$router.push(
              this.getEditorSectionCoverRoute({ sectionId: previousSection.id }),
            );
          }
        }
      }
    },
    navigateSectionNext() {
      const activityList = this.getActivityList(this.currentSectionId);

      if (activityList?.length > 0) {
        // Get the first activity in the current section
        const firstActivity = activityList[0];
        if (firstActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
          this.quizNavigatorStore.setCurrentPage(firstActivity.id, QUIZ_PAGE.QUIZ_COVER);
          return this.goToByPageIdAndQuizPageNo(firstActivity.id, QUIZ_PAGE.QUIZ_COVER);
        } else if (firstActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY) {
          if (this.isActivityHasPagination(firstActivity.id)) {
            this.activityComponentStore.setCurrentPageNo(firstActivity.uuid, 1);
            return this.goToByPageIdAndPageNo(firstActivity.id, 1);
          }
          return this.goToByPageId(firstActivity.id);
        }
      } else {
        const nextPageIndex = this.sectionData.pageNo;
        if (nextPageIndex < this.getSectionList.length) {
          const nextSection = this.getSectionList[nextPageIndex];
          return this.$router.push(this.getEditorSectionCoverRoute({ sectionId: nextSection.id }));
        }
      }
    },
    getActivityDefaultLabel(page) {
      return this.isSectionActivityNumberHidden
        ? ""
        : this.$i18n
            .t("bottom_navigation_default_label", {
              sectionLabel: StringUtils.getAlphabeticalLetter(this.sectionData.pageNo - 1),
              activityNo: page.pageNo,
            })
            .replace(/ /g, "\u00A0");
    },
    getActivityQuizEndLabel(page) {
      return this.isSectionActivityNumberHidden
        ? this.$i18n.t("bottom_navigation_quiz_end_with_no_section_label_or_activity_number_label")
        : this.$i18n
            .t("bottom_navigation_quiz_end_label", {
              sectionLabel: StringUtils.getAlphabeticalLetter(this.sectionData.pageNo - 1),
              activityNo: page.pageNo,
            })
            .replace(/ /g, "\u00A0");
    },
    getActivityQuizPageLabel(page) {
      const localizationKey = this.isSectionActivityNumberHidden
        ? "bottom_navigation_page_with_no_section_label_or_activity_number_label"
        : "bottom_navigation_page_label";

      return this.$i18n
        .t(localizationKey, {
          sectionLabel: StringUtils.getAlphabeticalLetter(this.sectionData.pageNo - 1),
          activityNo: page.pageNo,
          currentPageNo: this.currentPageNo + 1,
        })
        .replace(/ /g, "\u00A0");
    },
    getActivityPageLabel(page) {
      const localizationKey = this.isSectionActivityNumberHidden
        ? "bottom_navigation_page_with_no_section_label_or_activity_number_label"
        : "bottom_navigation_page_label";

      return this.$i18n
        .t(localizationKey, {
          sectionLabel: StringUtils.getAlphabeticalLetter(this.sectionData.pageNo - 1),
          activityNo: page.pageNo,
          currentPageNo: this.currentPageNo,
        })
        .replace(/ /g, "\u00A0");
    },
    getBottomNavigationSectionDefaultLabel(sectionPageNo, sectionTitle) {
      return this.isSectionActivityNumberHidden
        ? sectionTitle
        : this.$i18n.t("bottom_navigation_section_default_label", {
            sectionLabel: StringUtils.getAlphabeticalLetter(sectionPageNo),
            sectionTitle: sectionTitle,
          });
    },
    getBottomNavigationActivityQuizEndLabel(activityNumber) {
      return this.isSectionActivityNumberHidden
        ? this.$i18n.t("bottom_navigation_quiz_end_with_no_section_label_or_activity_number_label")
        : this.$i18n.t("bottom_navigation_activity_quiz_end_label", {
            activityNo: activityNumber,
          });
    },
    getBottomNavigationActivityPageLabel(activityNumber, activityTitle, currentPageNo) {
      const localizationKey =
        this.isSectionActivityNumberHidden || DeviceUtils.isDesktopLarge()
          ? "bottom_navigation_activity_with_page_label_no_activity_number"
          : "bottom_navigation_activity_with_page_label";

      return this.$i18n
        .t(localizationKey, {
          activityNo: activityNumber,
          activityTitle: activityTitle,
          currentPageNo: currentPageNo,
        })
        .replace(/ /g, "\u00A0");
    },
    getBottomNavigationActivityLabel(activityNumber, activityTitle) {
      return this.isSectionActivityNumberHidden
        ? activityTitle
        : this.$i18n.t("bottom_navigation_activity_label", {
            activityNo: activityNumber,
            activityTitle: activityTitle,
          });
    },
    getActivityLabel(currentPage) {
      if (AssignmentUtils.isActivityOrQuizActivity(currentPage?.type)) {
        if (currentPage.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
          if (this.currentPageNo === QUIZ_PAGE.QUIZ_COVER) {
            return this.getActivityDefaultLabel(currentPage);
          } else if (this.currentPageNo === QUIZ_PAGE.QUIZ_END) {
            return this.getActivityQuizEndLabel(currentPage);
          } else {
            return this.getActivityQuizPageLabel(currentPage);
          }
        } else {
          if (this.hasPagination) {
            return this.getActivityPageLabel(currentPage);
          }
          return this.getActivityDefaultLabel(currentPage);
        }
      }
    },
    navigateActivityPrevious(activity) {
      if (!AssignmentUtils.isActivityOrQuizActivity(activity.type)) {
        return;
      }

      const currentActivityIndex = activity.pageNo - 1;

      if (activity.type === LESSON_ACTIVITY_TYPE.ACTIVITY) {
        if (this.currentPageNo === 1) {
          if (activity.pageNo === 1) {
            return this.$router.push(
              this.getEditorSectionCoverRoute({ sectionId: this.sectionData.id }),
            );
          }
          // Not first activity and on first page, go to previous activity
          const previousActivity = this.activityList[currentActivityIndex - 1];
          if (previousActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
            this.quizNavigatorStore.setCurrentPage(previousActivity.id, QUIZ_PAGE.QUIZ_END);
            return this.goToByPageIdAndQuizPageNo(previousActivity.id, QUIZ_PAGE.QUIZ_END);
          } else if (previousActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY) {
            if (this.isActivityHasPagination(previousActivity.id)) {
              this.activityComponentStore.setCurrentPageNo(
                previousActivity.uuid,
                this.getActivityPageNo(previousActivity.id),
              );
              return this.goToByPageIdAndPageNo(
                previousActivity.id,
                this.getActivityPageNo(previousActivity.id),
              );
            } else {
              return this.goToByPageId(previousActivity.id);
            }
          }
        } else if (this.hasPagination && this.currentPageNo > 0) {
          // Not first page and has pagination, go to previous page of same activity
          this.activityComponentStore.setCurrentPageNo(activity.id, this.currentPageNo - 1);
          return this.goToByPageIdAndPageNo(activity.id, this.currentPageNo - 1);
        }
      } else if (activity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
        if (this.currentPageNo === QUIZ_PAGE.QUIZ_COVER) {
          if (activity.pageNo === 1) {
            return this.$router.push(
              this.getEditorSectionCoverRoute({ sectionId: this.sectionData.id }),
            );
          }
          // Not first quiz and on cover page, go to previous activity
          const previousActivity = this.activityList[currentActivityIndex - 1];
          if (previousActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
            this.quizNavigatorStore.setCurrentPage(previousActivity.id, QUIZ_PAGE.QUIZ_END);
            return this.goToByPageIdAndQuizPageNo(previousActivity.id, QUIZ_PAGE.QUIZ_END);
          } else if (previousActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY) {
            if (this.isActivityHasPagination(previousActivity.id)) {
              this.activityComponentStore.setCurrentPageNo(
                previousActivity.uuid,
                this.getActivityPageNo(previousActivity.id),
              );
              return this.goToByPageIdAndPageNo(
                previousActivity.id,
                this.getActivityPageNo(previousActivity.id),
              );
            } else {
              return this.goToByPageId(previousActivity.id);
            }
          }
        } else if (
          this.currentPageNo !== QUIZ_PAGE.QUIZ_COVER &&
          this.currentPageNo !== QUIZ_PAGE.QUIZ_END &&
          this.currentPageNo === 0
        ) {
          // On first page, go to quiz cover
          this.quizNavigatorStore.setCurrentPage(activity.id, QUIZ_PAGE.QUIZ_COVER);
          return this.goToByPageIdAndQuizPageNo(activity.id, QUIZ_PAGE.QUIZ_COVER);
        } else if (
          this.currentPageNo !== QUIZ_PAGE.QUIZ_COVER &&
          this.currentPageNo !== QUIZ_PAGE.QUIZ_END &&
          this.currentPageNo > 0
        ) {
          this.quizNavigatorStore.setCurrentPage(activity.id, this.currentPageNo - 1);
          return this.goToByPageIdAndQuizPageNo(activity.id, this.currentPageNo - 1);
        } else if (this.currentPageNo === QUIZ_PAGE.QUIZ_END) {
          if (this.getActivityPageNo(activity.id) === 0) {
            this.quizNavigatorStore.setCurrentPage(activity.id, QUIZ_PAGE.QUIZ_COVER);
            return this.goToByPageIdAndQuizPageNo(activity.id, QUIZ_PAGE.QUIZ_COVER);
          }
          this.quizNavigatorStore.setCurrentPage(
            activity.id,
            this.getActivityPageNo(activity.id) - 1,
          );
          return this.goToByPageIdAndQuizPageNo(
            activity.id,
            this.getActivityPageNo(activity.id) - 1,
          );
        }
      }
    },
    getActivityPreviousNavigationLabel(activity) {
      if (AssignmentUtils.isActivityOrQuizActivity(activity?.type)) {
        const currentActivityIndex = activity.pageNo - 1;

        if (activity.type === LESSON_ACTIVITY_TYPE.ACTIVITY) {
          if (this.currentPageNo === 1) {
            if (activity.pageNo === 1) {
              return this.getBottomNavigationSectionDefaultLabel(
                this.sectionData.pageNo - 1,
                this.sectionData.title,
              );
            }
            // Navigate to previous activity
            const previousActivity = this.activityList[currentActivityIndex - 1];

            if (previousActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
              // previous activity is quiz
              return this.getBottomNavigationActivityQuizEndLabel(previousActivity.pageNo);
            } else if (previousActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY) {
              // Check if the previous activity has multiple pages
              if (this.isActivityHasPagination(previousActivity.id)) {
                // Navigate to the last page of the previous activity
                return this.getBottomNavigationActivityPageLabel(
                  previousActivity.pageNo,
                  previousActivity.title,
                  this.getActivityPageNo(previousActivity.id),
                );
              } else {
                // The previous activity has no multiple pages
                return this.getBottomNavigationActivityLabel(
                  previousActivity.pageNo,
                  previousActivity.title,
                );
              }
            }
          } else if (this.hasPagination && this.currentPageNo > 1) {
            // Not first page and has pagination
            // Navigate to previous page of same activity
            return this.getBottomNavigationActivityPageLabel(
              activity.pageNo,
              activity.title,
              this.currentPageNo - 1,
            );
          } else {
            return this.getBottomNavigationActivityLabel(activity.pageNo, activity.title);
          }
        } else if (activity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
          if (this.currentPageNo === QUIZ_PAGE.QUIZ_COVER) {
            if (activity.pageNo === 1) {
              return this.getBottomNavigationSectionDefaultLabel(
                this.sectionData.pageNo - 1,
                this.sectionData.title,
              );
            }
            const previousActivity = this.activityList[currentActivityIndex - 1];

            if (previousActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
              // previous activity is quiz
              return this.getBottomNavigationActivityQuizEndLabel(previousActivity.pageNo);
            } else if (previousActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY) {
              // Check if the previous activity has multiple pages
              if (this.isActivityHasPagination(previousActivity.id)) {
                // Navigate to the last page of the previous activity
                return this.getBottomNavigationActivityPageLabel(
                  previousActivity.pageNo,
                  previousActivity.title,
                  this.getActivityPageNo(previousActivity.id),
                );
              } else {
                // The previous activity has no multiple pages
                return this.getBottomNavigationActivityLabel(
                  previousActivity.pageNo,
                  previousActivity.title,
                );
              }
            }
          } else if (
            this.currentPageNo !== QUIZ_PAGE.QUIZ_COVER &&
            this.currentPageNo !== QUIZ_PAGE.QUIZ_END &&
            this.currentPageNo === 0
          ) {
            return this.getBottomNavigationActivityLabel(activity.pageNo, activity.title);
          } else if (
            this.currentPageNo !== QUIZ_PAGE.QUIZ_COVER &&
            this.currentPageNo !== QUIZ_PAGE.QUIZ_END &&
            this.currentPageNo > 0
          ) {
            return this.getBottomNavigationActivityPageLabel(
              activity.pageNo,
              activity.title,
              this.currentPageNo,
            );
          } else if (this.currentPageNo === QUIZ_PAGE.QUIZ_END) {
            if (this.getActivityPageNo(activity.id) === 0) {
              return this.getBottomNavigationActivityLabel(activity.pageNo, activity.title);
            }
            return this.getBottomNavigationActivityPageLabel(
              activity.pageNo,
              activity.title,
              this.getActivityPageNo(activity.id),
            );
          }
        }
      }
    },
    navigateActivityNext(activity) {
      if (!AssignmentUtils.isActivityOrQuizActivity(activity.type)) {
        return;
      }
      const currentActivityIndex = activity.pageNo - 1;

      if (activity.type === LESSON_ACTIVITY_TYPE.ACTIVITY) {
        if (this.hasPagination) {
          if (this.currentPageNo < this.getActivityPageNo(activity.id)) {
            // Not on last page, go to next page of same activity
            this.activityComponentStore.setCurrentPageNo(activity.uuid, this.currentPageNo + 1);
            return this.goToByPageIdAndPageNo(activity.id, this.currentPageNo + 1);
          } else {
            // On last page, check for next activity or section
            if (activity.pageNo === this.totalNumActivities) {
              // Last activity in section, go to next section
              if (!this.isLastSection) {
                return this.$router.push(
                  this.getEditorSectionCoverRoute({ sectionId: this.nextSectionData.id }),
                );
              }
            } else {
              // Go to next activity
              const nextActivity = this.activityList[currentActivityIndex + 1];
              if (nextActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
                this.quizNavigatorStore.setCurrentPage(nextActivity.id, QUIZ_PAGE.QUIZ_COVER);
                return this.goToByPageIdAndQuizPageNo(nextActivity.id, QUIZ_PAGE.QUIZ_COVER);
              } else if (nextActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY) {
                if (this.isActivityHasPagination(nextActivity.id)) {
                  this.activityComponentStore.setCurrentPageNo(nextActivity.uuid, 1);
                  return this.goToByPageIdAndPageNo(nextActivity.id, 1);
                } else {
                  return this.goToByPageId(nextActivity.id);
                }
              }
            }
          }
        } else {
          // No pagination, go to next activity or section
          if (activity.pageNo === this.totalNumActivities) {
            // Last activity in section, go to next section
            if (!this.isLastSection) {
              return this.$router.push(
                this.getEditorSectionCoverRoute({ sectionId: this.nextSectionData.id }),
              );
            }
          } else {
            // Go to next activity
            const nextActivity = this.activityList[currentActivityIndex + 1];
            if (nextActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
              this.quizNavigatorStore.setCurrentPage(nextActivity.id, QUIZ_PAGE.QUIZ_COVER);
              return this.goToByPageIdAndQuizPageNo(nextActivity.id, QUIZ_PAGE.QUIZ_COVER);
            } else if (nextActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY) {
              if (this.isActivityHasPagination(nextActivity.id)) {
                this.activityComponentStore.setCurrentPageNo(nextActivity.uuid, 1);
                return this.goToByPageIdAndPageNo(nextActivity.id, 1);
              } else {
                return this.goToByPageId(nextActivity.id);
              }
            }
          }
        }
      } else if (activity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
        if (this.currentPageNo === QUIZ_PAGE.QUIZ_COVER) {
          if (this.getActivityPageNo(activity.id) === 0) {
            this.quizNavigatorStore.setCurrentPage(activity.id, QUIZ_PAGE.QUIZ_END);
            return this.goToByPageIdAndQuizPageNo(activity.id, QUIZ_PAGE.QUIZ_END);
          }
          // On cover page, go to first quiz question
          this.quizNavigatorStore.setCurrentPage(activity.id, 0);
          return this.goToByPageIdAndQuizPageNo(activity.id, 0);
        } else if (this.currentPageNo === QUIZ_PAGE.QUIZ_END) {
          // On end page, check for next activity or section
          if (activity.pageNo === this.totalNumActivities) {
            // Last activity in section, go to next section
            if (!this.isLastSection) {
              return this.$router.push(
                this.getEditorSectionCoverRoute({ sectionId: this.nextSectionData.id }),
              );
            }
          } else {
            // Go to next activity
            const nextActivity = this.activityList[currentActivityIndex + 1];
            if (nextActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
              this.quizNavigatorStore.setCurrentPage(nextActivity.id, QUIZ_PAGE.QUIZ_COVER);
              return this.goToByPageIdAndQuizPageNo(nextActivity.id, QUIZ_PAGE.QUIZ_COVER);
            } else if (nextActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY) {
              if (this.isActivityHasPagination(nextActivity.id)) {
                this.activityComponentStore.setCurrentPageNo(nextActivity.uuid, 1);
                return this.goToByPageIdAndPageNo(nextActivity.id, 1);
              } else {
                return this.goToByPageId(nextActivity.id);
              }
            }
          }
        } else if (this.currentPageNo < this.getActivityPageNo(activity.id) - 1) {
          // Not on last question, go to next question
          this.quizNavigatorStore.setCurrentPage(activity.id, this.currentPageNo + 1);
          return this.goToByPageIdAndQuizPageNo(activity.id, this.currentPageNo + 1);
        } else {
          // On last question, go to end page
          this.quizNavigatorStore.setCurrentPage(activity.id, QUIZ_PAGE.QUIZ_END);
          return this.goToByPageIdAndQuizPageNo(activity.id, QUIZ_PAGE.QUIZ_END);
        }
      }
    },
    getActivityNextNavigationLabel(activity) {
      if (AssignmentUtils.isActivityOrQuizActivity(activity?.type)) {
        const currentActivityIndex = activity.pageNo - 1;

        if (activity.type === LESSON_ACTIVITY_TYPE.ACTIVITY) {
          if (this.hasPagination) {
            if (this.currentPageNo < this.getActivityPageNo(activity.id)) {
              // Not on last page, go to next page of same activity
              return this.getBottomNavigationActivityPageLabel(
                activity.pageNo,
                activity.title,
                this.currentPageNo + 1,
              );
            } else {
              // On last page, check for next activity or section
              if (activity.pageNo === this.totalNumActivities) {
                // Last activity in section, go to next section
                if (!this.isLastSection) {
                  return this.getBottomNavigationSectionDefaultLabel(
                    this.nextSectionData.pageNo - 1,
                    this.nextSectionData.title,
                  );
                }
              } else {
                // Go to next activity
                const nextActivity = this.activityList[currentActivityIndex + 1];
                if (nextActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
                  return this.getBottomNavigationActivityLabel(
                    nextActivity.pageNo,
                    nextActivity.title,
                  );
                } else if (nextActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY) {
                  if (this.isActivityHasPagination(nextActivity.id)) {
                    return this.getBottomNavigationActivityPageLabel(
                      nextActivity.pageNo,
                      nextActivity.title,
                      1,
                    );
                  } else {
                    return this.getBottomNavigationActivityLabel(
                      nextActivity.pageNo,
                      nextActivity.title,
                    );
                  }
                }
              }
            }
          } else {
            // No pagination, go to next activity or section
            if (activity.pageNo === this.totalNumActivities) {
              // Last activity in section, go to next section
              if (!this.isLastSection) {
                return this.getBottomNavigationSectionDefaultLabel(
                  this.nextSectionData.pageNo - 1,
                  this.nextSectionData.title,
                );
              }
            } else {
              // Go to next activity
              const nextActivity = this.activityList[currentActivityIndex + 1];
              if (nextActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
                return this.getBottomNavigationActivityLabel(
                  nextActivity.pageNo,
                  nextActivity.title,
                );
              } else if (nextActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY) {
                if (this.isActivityHasPagination(nextActivity.id)) {
                  return this.getBottomNavigationActivityPageLabel(
                    nextActivity.pageNo,
                    nextActivity.title,
                    1,
                  );
                } else {
                  return this.getBottomNavigationActivityLabel(
                    nextActivity.pageNo,
                    nextActivity.title,
                  );
                }
              }
            }
          }
        } else if (activity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
          if (this.currentPageNo === QUIZ_PAGE.QUIZ_COVER) {
            if (this.getActivityPageNo(activity.id) === 0) {
              return this.getBottomNavigationActivityQuizEndLabel(activity.pageNo);
            }
            return this.getBottomNavigationActivityPageLabel(activity.pageNo, activity.title, 1);
          } else if (this.currentPageNo === QUIZ_PAGE.QUIZ_END) {
            // On end page, check for next activity or section
            if (activity.pageNo === this.totalNumActivities) {
              // Last activity in section, go to next section
              if (!this.isLastSection) {
                return this.getBottomNavigationSectionDefaultLabel(
                  this.nextSectionData.pageNo - 1,
                  this.nextSectionData.title,
                );
              }
            } else {
              // Go to next activity
              const nextActivity = this.activityList[currentActivityIndex + 1];
              if (nextActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
                return this.getBottomNavigationActivityLabel(
                  nextActivity.pageNo,
                  nextActivity.title,
                );
              } else if (nextActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY) {
                if (this.isActivityHasPagination(nextActivity.id)) {
                  return this.getBottomNavigationActivityPageLabel(
                    nextActivity.pageNo,
                    nextActivity.title,
                    1,
                  );
                } else {
                  return this.getBottomNavigationActivityLabel(
                    nextActivity.pageNo,
                    nextActivity.title,
                  );
                }
              }
            }
          } else if (this.currentPageNo < this.getActivityPageNo(activity.id) - 1) {
            return this.getBottomNavigationActivityPageLabel(
              activity.pageNo,
              activity.title,
              this.currentPageNo + 2,
            );
          } else {
            return this.$i18n.t("bottom_navigation_quiz_end_label", {
              activityNumber: activity.pageNo,
            });
          }
        }
      }
    },
  },
};
